import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useI18n } from '../hooks/useI18n';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Safely set text on a DOM element without breaking React reconciliation.
 * Uses nodeValue on existing text nodes instead of replacing them via textContent.
 */
function safeSetText(el, text) {
  // If the element has exactly one child that is a text node, update its nodeValue.
  // This preserves React's reference to the text node and avoids removeChild crashes.
  if (el.childNodes.length === 1 && el.childNodes[0].nodeType === 3) {
    el.childNodes[0].nodeValue = text;
  } else if (el.childNodes.length === 0) {
    // Empty element – safe to set textContent
    el.textContent = text;
  }
  // If element has multiple child nodes or non-text children, skip it entirely
  // to avoid destroying React-managed subtrees.
}

/**
 * Global component that loads and applies inline content edits for ALL visitors.
 * Runs on every page navigation and language change.
 *
 * SAFETY: Only modifies leaf text elements (no child elements) using nodeValue
 * to avoid destroying React-managed DOM subtrees which causes reconciliation crashes.
 */
export default function ContentEditsLoader() {
  const { lang } = useI18n();
  const location = useLocation();
  const appliedEdits = useRef(new Set());

  useEffect(() => {
    if (!lang) return;
    let cancelled = false;

    const applyEdits = async () => {
      try {
        const pageUrl = location.pathname;
        const res = await fetch(`${API}/api/content-editor/edits?page_url=${pageUrl}&language=${lang}`);
        if (!res.ok || cancelled) return;
        const data = await res.json();
        const edits = data.edits || [];
        if (edits.length === 0) return;
        // Delay to let React finish rendering first
        await new Promise(r => setTimeout(r, 800));
        if (cancelled) return;

        // Use requestAnimationFrame to avoid conflicting with React render cycle
        requestAnimationFrame(() => {
          if (cancelled) return;
          edits.forEach(edit => {
            try {
              let el;
              try {
                el = document.querySelector(edit.element_path);
              } catch {
                return;
              }
              if (!el || !edit.content) return;

              // Only modify leaf elements that have no child elements
              if (el.children && el.children.length > 0) return;

              const editKey = `${edit.element_path}::${lang}`;
              if (appliedEdits.current.has(editKey) && el.textContent === edit.content) return;

              safeSetText(el, edit.content);
              appliedEdits.current.add(editKey);
            } catch {
              // Silently skip any individual edit that fails
            }
          });
        });
      } catch {
        // Silently fail on network errors
      }
    };

    appliedEdits.current.clear();
    applyEdits();
    return () => { cancelled = true; };
  }, [lang, location.pathname]);

  return null;
}
